import { BoxModelIdSchema } from '@developerz/domain';
import { IconCopy, IconInfo } from '@developerz/ui';
import { createEffect, createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { API_BASE_URL, ApiError } from '../lib/api';
import { type EnrollRefusal, enrollRefusal, shownRefusal } from '../lib/enroll-refusal';
import { type EnrollmentMint, mintEnrollment } from '../lib/fleet';
import { buildConnectCommand } from '../lib/fleet-connect';
import { CodingModelLine } from './CodingModelLine';
import { EnrollRefusalAlert } from './EnrollRefusalAlert';
import { EnrollTokenReveal } from './EnrollTokenReveal';
import { Field } from './Field';
import { Button } from './ui/Button';

// Token transport for the Connect a machine body (#619, #6567). The person MAY type
// the box's coding model, then presses "Create setup command" (owner ruling G15):
// only then does it mint. A typed model rides the mint's body; a blank one lets the
// server prefill it from the account's verified keys (plan 102 slice 05). The mint
// answer's `coding_model` is what the command EXPORTS as `DZ_MODEL_OVERRIDE`, and
// `<CodingModelLine/>` says which model that is and where it came from; with no
// verified key the command keeps its own prompt. A non-blank id that fails
// `BoxModelIdSchema` (the join gate's own charset) shows an inline error and
// mints nothing.
//
// Mint a one-time `dev_ren_` enrollment token and show the connect command
// EXACTLY ONCE per mint. Supply-chain hardening (06b): the token never rides
// the command — URL query leaks to server logs, a literal leaks to shell
// history — so the command reads it interactively into the `X-Dz-Enroll-Token`
// header and the token is revealed SEPARATELY (its own copy) to paste at the
// command's prompt. The plaintext lives only in this component's signal, which
// the dialog disposes on close. A 402 surfaces the plan's limit as plain copy
// (`seat_cap`), or the named org-budget refusal (`lib/enroll-refusal.ts`).
// Minting happens ONLY on the click (not on open): each mint burns budget until
// its code expires (`expires_iso` on the mint, shown with the code), so an
// accidental open shouldn't.
//
// REPEATABLE, not batched (arc 101 slice 03 step 6). An enrollment token is
// single-use with a 1h TTL (`ENROLLMENT_TOKEN_TTL_SECONDS`,
// `packages/db/src/queries/runners.ts:83`), so four machines need four tokens
// either way. The choice was a `POST …/enrollments/batch` verb or this loop, and
// the loop wins on all three counts that matter:
//   - a `/v1` route is a one-way door, and this one would buy nothing the loop
//     does not: N single-use tokens still means N pastes on N machines;
//   - N plaintexts on screen at once is a strictly wider reveal-once blast
//     radius than one at a time — the exact thing the block above is guarding;
//   - the budget is per box. A batch of four against a budget of three has no
//     good answer (partial success, or refuse work the operator can do); the
//     loop lands the 402 on precisely the mint that exceeded the cap, carrying
//     the API's own limit.
// The command is IDENTICAL for every box — it carries no token — so it stays on
// screen across mints and only the revealed secret is replaced.

export type EnrollTransportTokenProps = {
  /** The dialog's open state — a close drops the plaintext (see below). */
  readonly open: boolean;
  readonly token: string | null;
  /** Called after a successful mint so the parent can refetch the roster. */
  readonly onMinted: () => void;
  readonly onClose: () => void;
  /**
   * The dialog shell that owns a plan line (`<ConnectMachineModal/>`): it is handed
   * every refusal this panel shows (and `null` when it clears), and a refusal about
   * the PLAN (`seat_cap`, `org_unfunded`) is then the shell's to say, once, in its
   * plan line, so this panel renders only the server's own line. Absent (the donate
   * wizard, which has no plan line), the panel renders every refusal itself.
   */
  readonly onRefusal?: ((refusal: EnrollRefusal | null) => void) | undefined;
};

/**
 * The join gate's model-id charset (`BOX_MODEL_ID_PATTERN`), as an HTML
 * `pattern` — a hint only, like `SshEnrollForm`'s. `BoxModelIdSchema` is the
 * check that enables the button, and the domain renderer re-gates the charset
 * before it exports anything.
 */
const MODEL_ID_PATTERN = '\\s*[A-Za-z0-9._:\\/\\-]+\\s*';

export function EnrollTransportToken(props: EnrollTransportTokenProps): JSX.Element {
  const [busy, setBusy] = createSignal(false);
  const [minted, setMinted] = createSignal<EnrollmentMint | null>(null);
  const [error, setError] = createSignal<EnrollRefusal | null>(null);
  const [copied, setCopied] = createSignal(false);
  /** The coding model the operator typed; an invalid one is never exported. */
  const [model, setModel] = createSignal('');
  /** Tokens minted since this dialog opened — the operator's place in the run. */
  const [mintCount, setMintCount] = createSignal(0);
  /** Blank (the server prefills), or a typed id the join gate admits. */
  const validModel = (): boolean =>
    model().trim() === '' || BoxModelIdSchema.safeParse(model().trim()).success;
  /** A typed id the gate would refuse: said inline, before anything is minted. */
  const modelError = (): string | null => (validModel() ? null : t('fleet.connect.model_invalid'));

  // Monotonic id for the current OPEN. A mint is a network round trip and both
  // footers can be clicked during it, so a token can land after the dialog that
  // asked for it has closed — and `setMinted` then would put a live plaintext
  // back on screen for the NEXT visit, which is the one thing the reset below
  // exists to prevent. Comparing `props.open` alone is not enough: a close and
  // a reopen inside the flight window reads `true` again.
  let session = 0;

  // Reveal-once: the plaintext dies with the dialog. This cannot ride on the
  // component's own disposal — the dialog delays unmount for its exit
  // animation, so reopening inside that window would greet the next visit with
  // the last token still on screen.
  createEffect(() => {
    if (!props.open) {
      session += 1;
      setMinted(null);
      setError(null);
      setCopied(false);
      setModel('');
      setMintCount(0);
    }
  });

  // Await-then-assign, deliberately: a refused repeat mint must leave the LIVE
  // token on screen. Clearing first would destroy a credential whose seat is
  // already burnt for an hour, over a 402 that says nothing about it.
  const mint = async (): Promise<void> => {
    if (busy() || !props.open || !validModel()) return;
    const mintedIn = session;
    setBusy(true);
    setError(null);
    try {
      const typed = model().trim();
      const next = await mintEnrollment(props.token, typed === '' ? undefined : typed);
      // Dropped on a closed session — the secret is unrecoverable, which IS the
      // contract. The seat it burnt is real either way, so the roster still gets
      // its refetch below; only the plaintext is discarded.
      if (session === mintedIn) {
        setMinted(next);
        setMintCount((n) => n + 1);
      }
      props.onMinted();
    } catch (e) {
      // Same closed-session rule as the success arm: a refusal for a dialog the
      // operator already dismissed must not greet the next one.
      if (session !== mintedIn) return;
      // 402 = the box budget; the problem carries which refusal and the
      // human message. Anything else is a failed mint with no line to trust.
      if (e instanceof ApiError && e.status === 402) {
        setError(enrollRefusal(e, t('fleet.addbox.seat_cap')));
      } else {
        setError({ kind: 'line', message: t('fleet.addbox.mint_error'), soft: false });
      }
    } finally {
      setBusy(false);
    }
  };

  // Token-independent: the command reads the token at a prompt (06b). The
  // typed model rides as an `export DZ_MODEL_OVERRIDE` ahead of it when it
  // passes the join gate's charset (`buildConnectCommand` is the seam, and it
  // refuses an invalid id rather than forging a shell line); blank or invalid
  // leaves the command's own `Coding model` prompt in charge.
  // The mint's answer names the model once there is one (`coding_model`, the typed
  // id echoed or the key catalog's); an answer without the field keeps the typed one.
  const oneLiner = (): string => {
    const answered = minted()?.coding_model;
    return buildConnectCommand(API_BASE_URL, answered ? (answered.id ?? undefined) : model());
  };

  const copy = (): void =>
    void navigator.clipboard?.writeText(oneLiner()).then(
      () => setCopied(true),
      () => setCopied(false),
    );

  // One refusal surface for both states. A repeat mint that fails changes
  // nothing else on screen, so without this the 402 that ENDS the run would be
  // indistinguishable from a button that did not fire.
  createEffect(() => props.onRefusal?.(error()));
  const errorAlert = (): JSX.Element => (
    <Show when={shownRefusal(error(), props.onRefusal !== undefined)}>
      {(refusal) => <EnrollRefusalAlert refusal={refusal()} />}
    </Show>
  );

  return (
    <Show
      when={minted()}
      fallback={
        // `.field-group` is the panel's vertical rhythm: the tab wrapper above
        // must stay unstyled so `hidden` can take it out of the a11y tree.
        <div class="field-group">
          <p class="field__desc">{t('fleet.addbox.token_intro')}</p>
          <Field
            label={t('fleet.connect.token_model_label')}
            id="connect-machine-model"
            desc={t('fleet.connect.model_hint')}
            error={modelError()}
          >
            {(a11y) => (
              <input
                {...a11y}
                class="input"
                type="text"
                value={model()}
                onInput={(e) => setModel(e.currentTarget.value)}
                placeholder={t('fleet.connect.model_placeholder')}
                pattern={MODEL_ID_PATTERN}
                maxLength={200}
              />
            )}
          </Field>
          {errorAlert()}
          <div class="dialog__footer">
            <Button variant="outline" onClick={props.onClose}>
              {t('fleet.addbox.cancel')}
            </Button>
            <Button disabled={busy() || !validModel()} onClick={() => void mint()}>
              {t(busy() ? 'fleet.addbox.minting' : 'fleet.connect.mint')}
            </Button>
          </div>
        </div>
      }
    >
      {(m) => (
        <div class="field-group">
          <div class="alert alert--icon" role="alert">
            <IconInfo />
            <p class="alert__title">{t('fleet.addbox.run_this')}</p>
            <p class="alert__desc">{t('fleet.addbox.reveal_once')}</p>
          </div>
          <pre class="code-block">
            <code>{oneLiner()}</code>
          </pre>
          <Show when={m().coding_model}>{(cm) => <CodingModelLine model={cm()} exported />}</Show>
          <EnrollTokenReveal
            token={m().enrollment_token}
            ordinal={mintCount()}
            expiresIso={m().expires_iso}
          />
          {errorAlert()}
          <p class="field__desc">{t('fleet.addbox.mint_next_hint')}</p>
          <div class="dialog__footer">
            <Button variant="outline" onClick={copy}>
              <IconCopy />
              {t(copied() ? 'fleet.addbox.copied' : 'fleet.addbox.copy')}
            </Button>
            <Button variant="outline" disabled={busy()} onClick={() => void mint()}>
              {t(busy() ? 'fleet.addbox.minting' : 'fleet.addbox.mint_next')}
            </Button>
            <Button onClick={props.onClose}>{t('fleet.addbox.done')}</Button>
          </div>
        </div>
      )}
    </Show>
  );
}
